// A product's Resources (decision 8; FEHome.dc.html's Resources, POYT.dc.html's tabs, the
// Accelerator's rows and AdminResources' rule for members): "Each section becomes a tab, in this
// order. Anything not open yet shows with a lock and when it opens. Empty sections are hidden."
// Pure: no network, no React, so a test can import it.
//
// - Tabs: the admin's sections in their order, each with what's open in it.
// - Opening later: the hub adds this tab after the sections whenever something is still locked
//   (the critic's settlement, 3 Oct), 1 card per week ("Week 4 worksheets and prompts") with when it
//   opens, and leaves it out when nothing is locked. A section left with nothing open is hidden.
// - Kept in their own tab instead (catalogue.settings.resources.locked_in_tabs, Profit On Your
//   Terms: POYT.dc.html draws each locked card in its own tab, Weekly packs' "Opens after the
//   session on 21 Oct" and Bonuses'): a locked item stays in its section, after what's open, with
//   its lock and when it opens, the tab counts both, and there's no Opening later tab.
// - A link to another product in the hub (the Accelerator's Training shelf: "Buy your next property
//   without a deposit", "Video, also in your library", a link to /masterclass) shows only to
//   someone who has that product open, so nobody lands on "nothing here" (the critic's for_the_batch
//   11). The screen says which paths they own (Resources.jsx).
// - Whether a placement has opened is the database's answer (data.open), never worked out here; the
//   line saying when it opens comes from the sessions they can see, or the week's name without them.
// - 5 kinds: a file downloads, a tool opens in a new tab (its page in the hub, where the tool runner
//   runs it: ToolPage.jsx), text copies, a link opens, a Vimeo video plays here. Yours for life
//   carries its badge.
import * as f from '../lib/format.js'
import { toolHref } from './model.js'

// What a kind's button says, before and after
export function actionFor(kind, title) {
  switch (kind) {
    case 'file': return { kind: 'download', label: 'Download', doneLabel: 'Downloaded' }
    case 'tool': return { kind: 'tool', label: 'Open', doneLabel: 'Opened in a new tab' }
    case 'text': return /prompt/i.test(title || '') ? { kind: 'copy', label: 'Copy prompt', doneLabel: 'Copied' } : { kind: 'copy', label: 'Copy', doneLabel: 'Copied' }
    case 'link': return { kind: 'link', label: 'Open', doneLabel: 'Opened in a new tab' }
    case 'video': return { kind: 'video', label: 'Watch', doneLabel: 'Watch' }
    default: return null
  }
}

// When a session-bound item opens: "Opens after tonight's session", "Opens after the session on
// 7 Oct", or with no session to name, "Opens after the Week 4 session"
function afterSessionLine(session, module, nowMs) {
  if (session?.starts_at) {
    const start = Date.parse(session.starts_at)
    const days = f.daysFrom(nowMs, start)
    if (days === 0) return f.uk(start).hh >= 17 ? "Opens after tonight's session" : "Opens after today's session"
    if (days > 0) return `Opens after the session on ${f.dayMonth(start)}`
  }
  if (module) return `Opens after the ${module.label} session`
  return 'Opens after the live session'
}

// The line under a locked item
export function whenLine(pl, data, nowMs) {
  if (pl.release_kind === 'on_date' && pl.release_at) {
    const at = Date.parse(pl.release_at)
    return f.daysFrom(nowMs, at) === 0 ? `Opens today at ${f.time(at)}` : `Opens on ${f.dayMonth(at)}`
  }
  if (pl.release_kind === 'after_session') {
    if (pl.release_session_id) {
      const s = (data.sessions || []).find(x => x.id === pl.release_session_id)
      const mod = s?.module_id ? (data.modules || []).find(m => m.id === s.module_id) : null
      return afterSessionLine(s, mod, nowMs)
    }
    const mod = (data.modules || []).find(m => m.id === pl.release_module_id) || null
    const s = (data.sessions || []).filter(x => x.module_id === pl.release_module_id && x.status !== 'cancelled' && x.starts_at)
      .sort((a, b) => Date.parse(a.starts_at) - Date.parse(b.starts_at))[0] || null
    return afterSessionLine(s, mod, nowMs)
  }
  return 'Opens soon'
}

// 1 item as a card
export function itemCard(pl, data, nowMs) {
  const item = (data.items || []).find(i => i.id === pl.item_id)
  if (!item) return null
  const body = (data.bodies || []).find(b => b.item_id === pl.item_id) || null
  const mod = pl.module_id ? (data.modules || []).find(m => m.id === pl.module_id) : null
  const open = data.open?.[pl.id] === true
  const title = pl.title_override || item.title
  const isNew = open && pl.new_until && Date.parse(pl.new_until) > nowMs
  const sub = pl.subline_override || item.subline || (mod ? mod.label : '')
  return {
    id: pl.id,
    itemId: item.id,
    kind: item.kind,
    title,
    meta: isNew && pl.created_at ? `New, added ${f.dayMonth(pl.created_at)}` : sub,
    isNew: Boolean(isNew),
    forLife: Boolean(pl.yours_for_life),
    open,
    when: open ? null : whenLine(pl, data, nowMs),
    action: open ? actionFor(item.kind, title) : null,
    text: open && item.kind === 'text' ? body?.text_body || '' : null,
    url: open && item.kind === 'link' ? body?.link_url || null : null,
    // a tool's page in the hub, opened in a new tab
    href: open && item.kind === 'tool' ? toolHref(data.product, pl.id) : null,
    vimeo: open && item.kind === 'video' ? body?.vimeo_url || null : null,
    moduleId: pl.module_id || null,
    sectionId: pl.section_id,
    releaseKey: pl.release_kind === 'after_session' ? (pl.release_module_id || pl.release_session_id) : `date:${pl.release_at || pl.id}`,
    releaseModuleId: pl.release_kind === 'after_session' ? pl.release_module_id || null : null,
  }
}

const KIND_WORDS = { file: 'worksheets', tool: 'tools', text: 'prompts', link: 'links', video: 'videos' }

// "Week 4 worksheets and prompts": what a week's locked items are, in 1 card
function groupTitle(module, cards) {
  const words = [...new Set(cards.map(c => KIND_WORDS[c.kind] || 'resources'))]
  const order = ['worksheets', 'prompts', 'tools', 'videos', 'links', 'resources']
  words.sort((a, b) => order.indexOf(a) - order.indexOf(b))
  const list = words.length > 2 ? `${words.slice(0, -1).join(', ')} and ${words[words.length - 1]}` : words.join(' and ')
  return `${module.label} ${list}`
}

// The meta line of a week's locked card: "Taught tonight", "Wed 7 Oct", or the week's own title
function groupMeta(module, data, nowMs) {
  const s = (data.sessions || []).filter(x => x.module_id === module.id && x.status !== 'cancelled' && x.starts_at)
    .sort((a, b) => Date.parse(a.starts_at) - Date.parse(b.starts_at))[0]
  if (s) {
    const days = f.daysFrom(nowMs, s.starts_at)
    if (days === 0) return f.uk(s.starts_at).hh >= 17 ? 'Taught tonight' : 'Taught today'
    return f.shortDate(s.starts_at)
  }
  return f.isPlaceholder(module.title) ? '' : module.title
}

// "The Property Accelerator terms and conditions": the product's own terms (catalogue.settings
// .terms_url, https only), beside Resources' tabs, or null
export function termsLink(product) {
  const url = product?.settings?.terms_url
  return typeof url === 'string' && /^https:\/\//.test(url) ? { href: url, label: `${product.name} terms and conditions` } : null
}

// A link to another product's page in the hub ('/masterclass', '/poyt/week-2'): its product's path,
// or null for any other link
export function productPathOf(url, productPaths) {
  if (typeof url !== 'string' || !url.startsWith('/') || url.startsWith('//')) return null
  const base = `/${url.split(/[/?#]/)[1] || ''}`
  return productPaths.includes(base) ? base : null
}

// The Resources tabs: [{ key, name, note, cards, count }]. Opening later's cards are each week's
// locked items in 1 card, or a locked item on its own.
//   lockedInTabs: keep a locked item in its own tab (default: the product's own setting)
//   productPaths, ownsPath(path): the hub's product paths, and whether they have that product open;
//     a link to a product path they don't own is left out. Without them, nothing is left out.
export function resourceTabs(data, nowMs, opts = {}) {
  const lockedInTabs = opts.lockedInTabs ?? Boolean(data.product?.settings?.resources?.locked_in_tabs)
  const paths = opts.productPaths || []
  const owns = opts.ownsPath || (() => true)
  const cards = (data.placements || [])
    .map(pl => itemCard(pl, data, nowMs))
    .filter(Boolean)
    .filter(c => {
      const path = c.kind === 'link' ? productPathOf(c.url, paths) : null
      return !path || owns(path)
    })
  const tabs = []
  for (const s of [...(data.sections || [])].sort((a, b) => a.sort_order - b.sort_order)) {
    const open = cards.filter(c => c.sectionId === s.id && c.open)
    const waiting = lockedInTabs ? cards.filter(c => c.sectionId === s.id && !c.open) : []
    const list = [...open, ...waiting]
    if (list.length) tabs.push({ key: s.id, name: s.name, note: s.tab_note || null, cards: list, count: list.length })
  }
  if (lockedInTabs) return tabs
  const locked = cards.filter(c => !c.open)
  if (locked.length) {
    const later = []
    const groups = new Map()
    for (const c of locked) {
      const mod = c.releaseModuleId ? (data.modules || []).find(m => m.id === c.releaseModuleId) : null
      if (!mod) { later.push({ ...c, sort: Infinity }); continue }
      if (!groups.has(mod.id)) groups.set(mod.id, { mod, cards: [] })
      groups.get(mod.id).cards.push(c)
    }
    for (const { mod, cards: list } of groups.values()) {
      later.push({
        id: `later-${mod.id}`, kind: 'locked', title: groupTitle(mod, list), meta: groupMeta(mod, data, nowMs),
        open: false, when: list[0].when, forLife: list.some(c => c.forLife), action: null, sort: mod.sort_order,
        items: list,
      })
    }
    later.sort((a, b) => a.sort - b.sort)
    tabs.push({ key: 'later', name: 'Opening later', note: null, cards: later, count: later.length })
  }
  return tabs
}

// The week page's own files and prompts: what's placed on the module (or the recording), open or
// not. Prompts are the text items; worksheets everything else.
export function weekResources(data, { moduleId, recordingId }, nowMs) {
  const list = (data.placements || [])
    .filter(pl => (recordingId && pl.recording_id === recordingId) || (moduleId && pl.module_id === moduleId && !pl.recording_id))
    .map(pl => itemCard(pl, data, nowMs))
    .filter(Boolean)
  return {
    sheets: list.filter(c => c.kind !== 'text'),
    prompts: list.filter(c => c.kind === 'text'),
  }
}

// "Pillar canvas, fillable PDF": a file's row on the week page, from its title and subline
// ("Fillable PDF, Week 2" without the week, its first letter small unless it's an acronym)
export function sheetLabel(card) {
  const format = String(card.meta || '').split(',')[0].trim()
  if (!format || /^(week|module)\s+\d+/i.test(format) || card.isNew) return card.title
  const small = /^[A-Z]{2}/.test(format) ? format : format.charAt(0).toLowerCase() + format.slice(1)
  return `${card.title}, ${small}`
}

